import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import type { Article } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? getCategoryBySlug(slug) : undefined;
  const categoryArticles: Article[] = slug ? getArticlesByCategory(slug) : [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-32">
          <span className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 mb-4">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </span>
          <h2 className="text-xl font-semibold text-foreground-900 mb-2">分类不存在</h2>
          <p className="text-foreground-600 mb-6">你找的分类页面不见了，可能已经被移除了</p>
          <Link
            to="/"
            className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const pageTitle = `${category.name} - 爆料吃瓜`;
  const pageDesc = `${category.description}，爆料吃瓜为你带来最新${category.name}资讯，海量瓜料每日更新。`;

  return (
    <>
      <head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta name="keywords" content={`${category.name},爆料吃瓜,娱乐八卦,${category.name}最新`} />
        <link rel="canonical" href={`https://bagua.example.com/category/${category.slug}`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={`https://bagua.example.com/category/${category.slug}`} />
        <meta property="og:type" content="website" />
      </head>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          {/* Category Header */}
          <section className="w-full px-4 md:px-6 pb-6">
            <div className="flex items-center gap-4 mb-6">
              <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 text-xl">
                <i className={category.icon}></i>
              </span>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">{category.description}</p>
              </div>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
              <Link
                to="/"
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                全部
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500'
                  }`}
                >
                  <i className={cat.icon}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </section>

          {/* Articles Grid */}
          <section className="w-full px-4 md:px-6 pb-16">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20">
                <span className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-inbox-line text-3xl"></i>
                </span>
                <p className="text-foreground-600 text-lg">该分类下暂无文章</p>
                <Link
                  to="/"
                  className="mt-4 text-primary-500 hover:text-primary-600 text-sm font-medium transition-colors"
                >
                  返回首页查看更多
                </Link>
              </div>
            )}
          </section>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}